import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
    useLoaderData,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import { useImmer } from 'use-immer';

import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import { NextAction } from '@r6digital/r6pay-types/types/Requests/PaymentLinkCreate';
import {
    creditCardType,
    validateCreditCardCSV,
    validateCreditCardNumber,
} from '@r6digital/rapidstor-types/react/entities/CreditCard';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import {
    useGetChargesQuery,
    useGetReservationQuery,
    usePostReservationFeeMutation,
    usePostScaReservationWithFeePrepareMutation,
} from '@/api/rapidstor';
import { WizardValidationError } from '@/types/Wizard';
import Logger from '@/utils/logging';
import { RapidStorSettings } from '@/utils/rapidstor';
import { getBackDestination } from '@/utils/routing';
import { normaliseFieldName, validateString } from '@/utils/validation';

import store from '@/store';
import { setUnitData } from '@/store/bookingSlice';

import Alert from '@/components/elements/alert';
import Button from '@/components/elements/button';
import Spinner from '@/components/elements/spinner';
import JourneyProgress from '@/components/JourneyProgress';
import WizardDescriptor from '@/components/rapidstor/wizard/descriptor';
import PaymentForm from '@/components/rapidstor/wizard/forms/payment-form';
import WizardPricingTable from '@/components/rapidstor/wizard/pricing-table';

import { transformCostRetrieve } from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { useR6PayContext } from '@/features/rapidstor/providers/R6PayServiceProvider';

import classes from '../scss/screens/reservation-with-fee.module.scss';

export default function ReservationWithFee() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const { unitList } = useContext(JourneyContext);
    const r6PayService = useR6PayContext(location);
    const error = searchParams.get('error');
    const error_message = searchParams.get('message');

    const [descriptor, setDescriptor] = useState<Descriptor>();
    const [unitType, setUnitType] = useState<UnitType>();
    const [reservation, setReservation] = useState<BaseReservation>();
    const [submittingPayment, setSubmittingPayment] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<WizardValidationError[]>([]);
    const [addFeeError, setAddFeeError] = useState<string>();

    const [prepareScaReservationWithFee] = usePostScaReservationWithFeePrepareMutation();
    const [updateReservationWithFee] = usePostReservationFeeMutation();

    const {
        data: res,
        isSuccess: onSuccess,
        error: reservationError,
    } = useGetReservationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            token: token,
        },
        {
            refetchOnMountOrArgChange: reservation?.token !== token,
        }
    );

    const { data: charges, error: chargesError } = useGetChargesQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
        },
        {
            skip: !onSuccess,
        }
    );

    const [form, updateForm] = useImmer<Record<string, string>>({
        cardHolderName: '',
        cardNumber: '',
        cardExpiryMonth: '',
        cardExpiryYear: '',
        cardCsv: '',
    });

    const [, setCookie] = useCookies(['reservation']);

    const updateExpiryDate = (value: Record<string, string | number>) => {
        if (!value.floatValue) {
            updateForm((draft) => {
                draft.cardExpiryMonth = '';
                draft.cardExpiryYear = '';
                return draft;
            });
        } else {
            updateForm((draft) => {
                draft.cardExpiryMonth = String(value.formattedValue).split('/')[0];
                draft.cardExpiryYear = String(value.formattedValue).split('/')[1];
                return draft;
            });
        }
    };

    const update = (key: string, event: React.BaseSyntheticEvent) => {
        updateForm((draft) => {
            if (key === 'cardNumber') {
                const value = String(event.target.value).replace(' ', '');
                draft[key] = value;
            } else {
                draft[key] = String(event.target.value);
            }
            return draft;
        });
    };

    const checkCookie = () => {
        if (reservation) {
            // Set Cookie
            setCookie(
                'reservation',
                {
                    token: token,
                    id: reservation._id,
                },
                {
                    path: window.location.pathname,
                    maxAge: 60 * 60 * 24 * 7,
                }
            );
            setupSliceData();
        }
    };

    const setupSliceData = () => {
        // Set UnitData
        if (descriptor && unitType && unitList) {
            store.dispatch(
                setUnitData({
                    location,
                    unitType: unitType,
                    descriptor,
                    descriptors: unitList.descriptors,
                })
            );
        }
    };

    const initiateR6Pay = async () => {
        await r6PayService.service?.getPaymentMethods();
    };

    useEffect(() => {
        setReservation(res);
    }, [onSuccess, res]);

    useEffect(() => {
        if (!token || !window.rapidstor_settings.rapidstor_options.corpToken) {
            navigate('/');
        } else {
            setReservation(res);
            checkCookie();

            if (
                location.rapidstor.paymentGateways.use &&
                location.rapidstor.paymentGateways.gateways.r6Pay &&
                !r6PayService.service
            ) {
                initiateR6Pay();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (reservation && unitList) {
            unitList.descriptors.map((descriptor: Descriptor) => {
                const found = descriptor.unitTypes.find((uT: UnitType) => {
                    return uT._id === reservation.unitType._id;
                });
                if (found) {
                    setUnitType(found);
                    setDescriptor(descriptor);
                    checkCookie();
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitList, reservation]);

    const totalCost = useMemo(() => {
        if (!charges) return 0;
        return transformCostRetrieve(
            charges,
            undefined,
            undefined,
            0
        ).reduce((acc, item) => {
            return acc + item.chargeAmount + item.taxAmount - item.discountAmount;
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charges]);

    const isStep2 = useMemo(() => {
        return window.location.hash.includes('/return');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.hash]);

    useEffect(() => {
        if (isStep2) {
            document.getElementById('confirmation-panel')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [isStep2]);

    const validateForm = (): WizardValidationError[] => {
        setValidationErrors([]);
        const errors: WizardValidationError[] = [];

        const formKeys = Object.keys(form);

        const usingPaymentGateway = (location.rapidstor.paymentGateways.use &&
            location.rapidstor.paymentGateways.gateways.r6Pay);

        if (usingPaymentGateway) {
            setValidationErrors([]);
            return [];
        }

        for (const key of formKeys) {
            const normalisedKey = normaliseFieldName(key);

            if (!validateString(form[key])) {
                errors.push({
                    name: key,
                    message: `${normalisedKey} is required`,
                });
            }
        }

        setValidationErrors(errors);

        return errors;
    };

    const payViaGateway = async (payment_method: PaymentProcessor) => {
        if (
            location.rapidstor.paymentGateways.use &&
            location.rapidstor.paymentGateways.gateways.r6Pay &&
            reservation
        ) {
            const returnUrl = (): string => {
                return `https://${window.location.host}${window.location.pathname}${window.location.search}#/api/payment-redirect/${reservation.token}/reservation-with-fee`;
            };

            const payload = r6PayService.service.buildCreatePaymentLinkPayload(
                reservation,
                form,
                location,
                location.rapidstor.paymentGateways.gateways.r6Pay.currency,
                returnUrl(),
                payment_method,
                totalCost
            );

            try {
                const result = await r6PayService.service.createPaymentLink(payload);
                if (!result) {
                    Logger.error(`[processBooking] Failed R6Pay request`, location, [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        }
                    ]);
                    setAddFeeError('R6Pay Create Payment Error');
                    return;
                }
                if (result.next_action.type === NextAction.REDIRECT_TO_URL) {
                    window.location.href = result.next_action.url;
                    return;
                }
            } catch (error) {
                Logger.error(
                    `[processBooking] Failed R6Pay request: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]
                );
                setAddFeeError('R6Pay Create Payment Error');
                return;
            }
        }
    }

    const submitPayment = async (payment_method?: PaymentProcessor, e?: React.FormEvent<HTMLFormElement>) => {
        if (e) {
            e.preventDefault();
        }
        setSubmittingPayment(true);
        setAddFeeError(undefined);

        const errors = validateForm();

        if (errors.length) {
            setSubmittingPayment(false);
            return;
        }

        if (payment_method) {
            await payViaGateway(payment_method);
        } else {
            const cc = creditCardType(form.cardNumber);

            if (!validateCreditCardNumber(form.cardNumber, location) || !cc) {
                errors.push({
                    name: 'cardNumber',
                    message: 'Invalid card number',
                });
                return;
            }

            const formattedExpiryMonth = moment(form.cardExpiryMonth, 'MM').format('MM');
            const formattedExpiryYear = moment(form.cardExpiryYear, 'YYYY').format('YYYY');
            const lastDayOfMonth = moment(
                `${formattedExpiryYear}-${formattedExpiryMonth}`
            ).endOf('month');
            const today = moment();

            if (lastDayOfMonth < today) {
                errors.push({
                    name: 'cardExpiry',
                    message: 'Card has expired',
                });
            }

            if (cc && !validateCreditCardCSV(cc, form.cardCsv)) {
                errors.push({
                    name: 'cardCsv',
                    message: 'Invalid CSV',
                });
            }

            if (errors.length > 0) {
                setValidationErrors(errors);
                setSubmittingPayment(false);
                return;
            } else {
                setValidationErrors([]);
            }

            if (!reservation?.globalWaitingNum || !reservation._id) {
                setSubmittingPayment(false);
                setAddFeeError(
                    'An error occured while processing the reservation, please try again or contact support.'
                );
                return;
            }
            if (Object.keys(location.scaConfiguration).length) {
                try {
                    const result = await prepareScaReservationWithFee({
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        payload: {
                            locationCode: location.locationCode,
                            cardCVV: form.cardCsv,
                            cardExpiryMonth: form.cardExpiryMonth,
                            cardExpiryYear: form.cardExpiryYear,
                            cardHoldersName: form.cardHolderName,
                            cardNumber: form.cardNumber,
                            chargeTotal: totalCost,
                            cardTypeId: cc,
                            view: 'standalone',
                            waitingNum: reservation.globalWaitingNum,
                            reservationId: reservation._id,
                            standaloneReturnUrl: `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}#/api/payment-redirect/${reservation.token}/reservation-with-fee`,
                        },
                    }).unwrap();

                    if (result?.redirect) {
                        Logger.info('[submitPayment] Redirecting to SCA payment URL');
                        window.location.href = result.redirect;
                        setSubmittingPayment(false);
                    } else {
                        Logger.warn('An error occued while preparing SCA payment', location, [
                            {
                                name: 'result',
                                value: JSON.stringify(result),
                            },
                        ]);
                        setAddFeeError(
                            'An error occured while preparing SCA payment, please try again or contact support.'
                        );
                        setSubmittingPayment(false);
                        return;
                    }
                } catch (err) {
                    Logger.error(
                        `An error occured while processing a reservation with fee: ${JSON.stringify(
                            err
                        )}`,
                        location
                    );
                    setAddFeeError(`${err}`);
                    setSubmittingPayment(false);
                }
            } else {
                try {
                    const result = await updateReservationWithFee({
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        token: reservation._id,
                        payload: {
                            card: {
                                cvv: form.cardCsv,
                                expiryYear: form.cardExpiryYear,
                                expiryMonth: form.cardExpiryMonth,
                                name: form.cardHolderName,
                                number: form.cardNumber,
                                type: cc,
                            },
                            reservationId: reservation._id,
                        },
                    }).unwrap();

                    setSubmittingPayment(false);
                    Logger.info('[submitPayment] Payment successful');

                    navigate(`/api/payment-redirect/${result.token}/reservation-with-fee`);
                } catch (err) {
                    Logger.error(
                        `An error occured while processing a reservation with fee: ${JSON.stringify(
                            error
                        )}`,
                        location
                    );
                    setAddFeeError(`${error}`);
                    setSubmittingPayment(false);
                }
            }
        }
    };

    const windowLocation = useLocation();

    const goBack = () => {
        navigate(getBackDestination(windowLocation.pathname));
    };

    const confirmBookingButtonText = (confirmText?: string): string => {
        return t(`wizard:${confirmText || 'next'}`);
    };

    useEffect(() => {
        if (error || error_message) {
            setAddFeeError(`${error_message}`);
        }
    }, [error, error_message]);

    return (
        <div className={`${classes.container} ${submittingPayment && classes.disabled}`}>
            <div className={`${classes.header}`}>
                <div>
                    {!isStep2 && (
                        <Button
                            type={'primaryOnWhite'}
                            size={'medium'}
                            chevron={'left'}
                            onclick={goBack}
                        >
                            <span>{t('wizard:back')}</span>
                        </Button>
                    )}
                </div>
                <span className={`${classes.title}`}>{t('wizard:reservation')}</span>
                <div></div>
            </div>
            <JourneyProgress step={isStep2 ? 2 : 1} stepsType={'reservation-with-fee'} />

            {reservationError || chargesError ? (
                <div id='error-page' className={classes.error_page}>
                    <span className={classes.header}>{t('error:oops')}</span>
                    <p className={classes.p}>{t('error:subtitle')}</p>
                    <p className={classes.p1}>{t('error:general_error')}</p>
                    <Button onclick={() => navigate('/')} type='primary' size='medium'>
                        {t('error:go_back')}
                    </Button>
                </div>
            ) : (
                <>
                    {isStep2 && (
                        <div className={`${classes.confirmation}`} id='confirmation-panel'>
                            {error ? (
                                <div id='error-page' className={classes.error_page}>
                                    <span className={classes.header}>{t('error:oops')}</span>
                                    <p className={classes.p}>{t('error:subtitle')}</p>
                                    <p className={classes.p1}>{error_message}</p>
                                    <Button
                                        onclick={() => navigate('/')}
                                        type='primary'
                                        size='medium'
                                    >
                                        {t('error:go_back')}
                                    </Button>
                                </div>
                            ) : (
                                <div className={`${classes.success}`}>
                                    <div className={`${classes.top_container}`}>
                                        <span className={`${classes.orderNumber}`}>
                                            {t('wizard:reservationWithFee:orderNumber')}
                                        </span>
                                        <span className={`${classes.waitingNum}`}>{reservation?.globalWaitingNum}</span>
                                    </div>
                                    <span className={`${classes.confirmation_text}`}>
                                        {t('wizard:reservationWithFee:confirmationText')}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {isStep2 && !error && <div className={`${classes.fold}`}></div>}

                    {!error && (
                        <WizardDescriptor
                            descriptor={descriptor}
                            unitType={unitType}
                            deal={reservation?.deal}
                        />
                    )}

                    {!isStep2 && <div className={`${classes.fold}`}></div>}

                    {reservation && !isStep2 && (
                        <span className={classes.copy}>{t('wizard:copy')}</span>
                    )}

                    {!isStep2 && (
                        <>
                            <WizardPricingTable
                                costRetrieve={undefined}
                                charges={charges}
                                merchandiseDiscount={0}
                            />

                            <div
                                className={`${classes.payment_container}`}
                                hidden={!descriptor || !charges}
                            >
                                <span className={`${classes.header}`}>{t('journey_bar:step_5')}</span>
                                {addFeeError && <Alert type='error' text={addFeeError} />}

                                {RapidStorSettings(location).paymentForm().show() ? (
                                    <>
                                        {submittingPayment ? (
                                            <Spinner size='80' />
                                        ) : (
                                            <PaymentForm
                                                onSubmit={(e) => submitPayment(undefined, e)}
                                                form={form}
                                                submittingPayment={submittingPayment}
                                                update={update}
                                                updateExpiryDate={updateExpiryDate}
                                                validationErrors={validationErrors}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <div className={`${classes.payViaGateway}`}>
                                        {r6PayService.service?.paymentMethods?.map((method) => (
                                            <Button
                                                key={method.id}
                                                disableScroll
                                                onclick={() => {
                                                    submitPayment(method.type)
                                                }}
                                                type={'primary'}
                                                size={'medium'}
                                                disabled={submittingPayment}
                                                style={{ marginBottom: '12px' }}
                                            >
                                                {submittingPayment ? (
                                                <Spinner size='25' inverted />
                                            ) : (
                                                <>{confirmBookingButtonText(method.custom_fields.display_name)}</>
                                            )}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
